<script setup lang="ts">
import HomeImage from "@/components/images/HomeImage.vue";
import ForgotPassword from "@/components/auth/ForgotPassword.vue";
import BackIcon from "@/components/icons/BackIcon.vue";
import SignIn from "@/components/auth/SignIn.vue";
import OTPCheck from "@/components/auth/OTPCheck.vue";
import ChangePassword from "@/components/auth/ChangePassword.vue";
import {onActivated, type Ref, ref, watch} from "vue";
import Translator from "@/utils/common/Translator";
import ThemeToggleButton from "@/components/inputs/ThemeToggleButton.vue";
import LoginUtils from "@/utils/login/LoginUtils";
import {useLoadingStore} from "@/stores/LoadingStore";
import {useNotificationsStore} from "@/stores/ToastDataStore";
import {useLoginInfoStore} from "@/stores/loginInfo";
import GUIUtilities from "@/utils/common/GUIUtilities.ts";
import ChangeLanguage from "@/components/navBar/languages/ChangeLanguage.vue";
import {Dialog} from "quasar";
import AboutNamaDialog from "@/components/dialogs/AboutNamaDialog.vue";
import {enableQrLoginDialogKeyboardShortcut} from "@/utils/common/ShortCutsUtil.ts";
import {useThemeStore} from "@/stores/ThemeDataStore.ts";
import PlaceTokens from "@/utils/constants/PlaceTokens.ts";
import router from "@/router";
import {AuthScreenType} from "@/utils/UIConstants/enums.ts";

let themeStore = useThemeStore();

let urlToken = "";
let name: string = "";
let pass: string = "";
let stay: boolean = false;
let resendDelay: number = 60;
let otpLength: number = 6;
let userNameForForgotPassword = "";
let emailOrPhoneValue = "";
let isEmailValue: boolean = true;
let otpId: string = "";
let loadingStore = useLoadingStore();
const screenType: Ref<AuthScreenType> = ref(resolveInitialAuthMode());

function resolveInitialAuthMode(): AuthScreenType {
  const route = router.currentRoute.value;
  const resetToken = route.query[PlaceTokens.PRM_RESET_TOKEN];
  const resetUser = route.query[PlaceTokens.PRM_RESET_USER];
  if (resetToken && resetUser) {
    urlToken = resetToken as string;
    userNameForForgotPassword = resetUser as string;
    return AuthScreenType.ChangePassword;
  }
  return AuthScreenType.SignIn;
}

watch(() => router.currentRoute.value, () => {
  screenType.value = resolveInitialAuthMode();
})

onActivated(() => {
  GUIUtilities.updateWindowTitle(Translator.translateId('login'));
})

function onBackClick() {
  name = pass = otpId = "";
  screenType.value = AuthScreenType.SignIn;
  router.push({
    name: router.currentRoute.value.name,
    query: {}
  });
}

async function onSignInClicked(username: string, password: string, staySignedIn: boolean) {
  loadingStore.startLoading();
  let extraInfo: Record<any, any> | undefined = await LoginUtils.login(username, password, staySignedIn);
  if (extraInfo && extraInfo.otpId) {
    otpId = extraInfo.otpId;
    resendDelay = +extraInfo.otpResendDelay;
    otpLength = +extraInfo.otpLength;
    name = username;
    pass = password;
    stay = staySignedIn;
    let isEstidamahWithoutOtp = extraInfo.isEstidamahWithoutOtp;
    if (isEstidamahWithoutOtp === "true")
      await onConfirmOTPClicked("0000");
    else
      screenType.value = AuthScreenType.OTPCheck;
  }
  loadingStore.stopLoading();
  useNotificationsStore().pushNotifications(useLoginInfoStore().notifications??[]);
}

function onForgotPasswordClicked(username: string) {
  userNameForForgotPassword = username;
  screenType.value = AuthScreenType.ForgotPassword;
}

async function onSendResetTokenClicked(emailOrPhone: string, isEmail: boolean) {
  loadingStore.startLoading();
  emailOrPhoneValue = emailOrPhone;
  isEmailValue = isEmail;
  let isValidData = await LoginUtils.sendResetToken(emailOrPhone, isEmail, userNameForForgotPassword);
  if (isValidData)
    screenType.value = AuthScreenType.ChangePassword;
  loadingStore.stopLoading();
}

async function onConfirmOTPClicked(otp: string) {
  loadingStore.startLoading();
  let response = await LoginUtils.login(name, pass, stay, otpId, otp);
  if (response && !response.otpId)
    name = pass = otpId = "";
  loadingStore.stopLoading();

}

async function onResendOTPClicked() {
  loadingStore.startLoading();
  otpId = await LoginUtils.resendOtpMsg(name, otpId);
  loadingStore.stopLoading();
}

async function onConfirmChangePassword(password: string, reTypePassword: string, token: string) {
  loadingStore.startLoading();
  let isValidData = await LoginUtils.resetPassword(userNameForForgotPassword, token, password, reTypePassword);
  if (isValidData)
    screenType.value = AuthScreenType.SignIn
  loadingStore.stopLoading();
}

enableQrLoginDialogKeyboardShortcut()

async function onLogoRightClick() {
  Dialog.create({
    component: AboutNamaDialog
  })
}
</script>

<template>
  <div class="tw-flex tw-w-full tw-flex-col tw-overflow-x-hidden
              mobile:tw-min-h-[100dvh]
              desktop:tw-h-screen desktop:tw-overflow-hidden">
    <div class="tw-hidden tw-w-full tw-flex-none tw-justify-between tw-px-4 tw-pt-6 mobile:tw-flex">
      <div class="tw-w-fit">
        <a v-if="screenType != AuthScreenType.SignIn"
           href="#"
           class="tw-flex tw-min-h-11 tw-items-center"
           @click.prevent="onBackClick">
          <BackIcon/>
          <p class="tw-m-0 tw-ms-2 tw-font-bold dark:tw-text-namaFontColor-input-dark">
            {{ Translator.translateId("back") }}
          </p>
        </a>
      </div>
      <div class="tw-flex tw-items-center tw-gap-3">
        <ChangeLanguage is-not-auth class="[&>svg]:tw-min-w-7 [&>svg]:tw-min-h-7 !tw-py-0" :dir="Translator.isEnglish()?'rtl':'ltr'"/>
        <ThemeToggleButton/>
      </div>
    </div>
    <div class="tw-flex tw-min-h-0 tw-flex-1 tw-flex-row mobile:tw-flex-col">
      <div class="tw-flex tw-w-3/5 mobile:tw-w-full mobile:tw-flex-none"
           @contextmenu="onLogoRightClick">
        <HomeImage :primary-color="themeStore.getPrimaryColor()" :secondary-color="themeStore.getPrimaryDarkColor()"/>
      </div>
      <div class="tw-flex tw-w-2/5 tw-min-h-0 tw-flex-col mobile:tw-w-full desktop:tw-overflow-y-auto">
        <div class="tw-flex tw-w-full tw-flex-none tw-justify-between tw-px-5 tw-pt-5 mobile:tw-hidden">
          <div class="tw-w-fit">
            <a v-if="screenType != AuthScreenType.SignIn"
               href="#"
               class="tw-flex tw-w-fit tw-items-center"
               @click.prevent="onBackClick">
              <BackIcon/>
              <p class="tw-m-0 tw-ms-2 tw-font-bold dark:tw-text-namaFontColor-input-dark">
                {{ Translator.translateId("back") }}
              </p>
            </a>
          </div>
          <div class="tw-flex tw-items-center tw-gap-3">
            <ChangeLanguage is-not-auth class="[&>svg]:tw-min-w-7 [&>svg]:tw-min-h-7 !tw-py-0" :dir="Translator.isEnglish()?'rtl':'ltr'"/>
            <ThemeToggleButton/>
          </div>
        </div>
        <SignIn v-if="screenType==AuthScreenType.SignIn" @signInClicked="onSignInClicked"
                @forgotPasswordClicked="onForgotPasswordClicked" @logoRightClick="onLogoRightClick"/>
        <ForgotPassword v-else-if="screenType==AuthScreenType.ForgotPassword" @onSendResetTokenClicked="onSendResetTokenClicked"/>
        <OTPCheck v-else-if="screenType==AuthScreenType.OTPCheck"
                  :otpLength="otpLength" :resendDelay="resendDelay" @confirmOTPClicked="onConfirmOTPClicked" @resendOTPClicked="onResendOTPClicked"/>
        <ChangePassword v-else-if="screenType==AuthScreenType.ChangePassword" @confirmChangePassword="onConfirmChangePassword" :urlToken="urlToken"/>
      </div>
    </div>
  </div>
</template>

<style scoped>

</style>
